import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import LogoBanner from "./LogoBanner";
import Marquee from "./Marquee";
import { CASE_STUDIES } from "../data/projects";
import { apiFile, fetchAds } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];

const TILE_SPAN = "col-span-1 md:col-span-6";

const FOCUS = [
  { img: `/assets/img/shmueli-uk-t.webp`, title: "Shmueli — Bein Hazmanim" },
  { img: `/assets/img/suited-london-new-season-t.webp`, title: "Suited London" },
  { img: `/assets/img/chap-a-ride-raffle-ad-2-t.webp`, title: "Chap a Ride" },
  { img: `/assets/img/kytchn-intro-ad-2-t.webp`, title: "Kytchn" },
  { img: `/assets/img/herring11-t.webp`, title: "Relish" },
  { img: `/assets/img/embellish-milky2-t.webp`, title: "Embellish" },
  { img: `/assets/img/suited-london-sukkos-5787-2-t.webp`, title: "Suited London" },
  { img: `/assets/img/embellish-purim-seuda-page-1-t.webp`, title: "Embellish" },
  { img: `/assets/img/car-ad-3-t.webp`, title: "Chap a Ride" },
  { img: `/assets/img/furnisure-furnish-faster-1-t.webp`, title: "Furnisure" },
  { img: `/assets/img/laser-by-chava-anniversary-sale-t.webp`, title: "Laser by Chava" },
  { img: `/assets/img/rulop-nuts-launch-t.webp`, title: "Rulop" },
  { img: `/assets/img/furnisure-van-art-t.webp`, title: "Furnisure van wrap", pos: "20% center" },
  { img: `/assets/img/horizon-thank-you-ad-status-1-t.webp`, title: "Camp Horizon" },
  { img: `/assets/img/chinuch-match-t.webp`, title: "Chinuch Match" },
  { img: `/assets/img/relish-pre-pesach-t.webp`, title: "Relish" },
  { img: `/assets/img/wigline-artwork-final-version-t.webp`, title: "WigLine" },
];

function CaseTile({ project }) {
  const navigate = useNavigate();
  return (
    <motion.a
      href={`/work/${project.id}`}
      onClick={(e) => {
        e.preventDefault();
        navigate(`/work/${project.id}`);
      }}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: EASE }}
      data-testid={`case-card-${project.index}`}
      className={`group relative ${TILE_SPAN} block aspect-[4/3] overflow-hidden bg-panel transition-opacity duration-200 active:opacity-80`}
    >
      <div className="absolute inset-0" style={{ background: project.tile }} />
      {project.tileArt && (
        <div
          className="absolute inset-0 transition-transform duration-[1100ms] ease-out group-hover:[--tx:-1%] group-hover:[--ty:-1.6%]"
          style={{
            transform: "translate3d(var(--tx, 0px), var(--ty, 0px), 0) scale(var(--tz, 1))",
            transformOrigin: project.tileArt.pos,
            "--tz": project.tileArt.zoom,
          }}
        >
          <img
            src={project.tileArt.src}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.03]"
            style={{ objectPosition: project.tileArt.pos }}
          />
        </div>
      )}

      <div className="pointer-events-none absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/10" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent transition-opacity duration-500 md:opacity-0 md:group-hover:opacity-100" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-full p-5 md:p-7">
        <div className="translate-y-0 transition-all duration-500 group-hover:-translate-y-1 md:translate-y-3 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100">
          <h3
            className="font-display font-extrabold uppercase leading-none tracking-tight text-left text-white text-[clamp(9px,3vw,12px)] sm:text-xl md:text-3xl"
          >
            {project.title}
          </h3>
          <p
            className="mt-2 hidden font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 sm:block md:text-xs"
          >
            {project.landscape
              ? `${project.subtitle} · View Case`
              : `${project.subtitle} — ${project.posters.length} Poster${project.posters.length > 1 ? "s" : ""} · View Case`}
          </p>
        </div>
      </div>
    </motion.a>
  );
}

export default function Work() {
  const [isPhone, setIsPhone] = useState(() => window.matchMedia("(max-width: 639.98px)").matches);
  const [remoteAds, setRemoteAds] = useState([]);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639.98px)");
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener("change", onChange);
    let alive = true;
    fetchAds().then((ads) => { if (alive) setRemoteAds(ads); });
    return () => {
      mq.removeEventListener("change", onChange);
      alive = false;
    };
  }, []);
  const baseAds = useMemo(
    () => [...remoteAds, ...FOCUS],
    [remoteAds]
  );
  const focusAds = useMemo(() => {
    if (!isPhone) return baseAds;
    const a = [...baseAds];
    const off = remoteAds.length;
    [a[4 + off], a[5 + off]] = [a[5 + off], a[4 + off]];
    return a;
  }, [isPhone, baseAds, remoteAds.length]);
  return (
    <section id="work" data-testid="portfolio-section" className="px-6 py-16 md:py-24 lg:px-12 lg:py-36">
      <LogoBanner />

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="mb-12 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 md:mb-16"
      >
        <span>Scroll</span>
        <span>Posters · Brochures · Single Ads</span>
      </motion.div>

      <div className="mb-12 flex flex-col gap-8 md:mb-16 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="mb-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-vermilion md:text-xs">
            <span className="inline-block h-2 w-2 bg-vermilion" />
            Case Studies
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-display text-5xl font-extrabold uppercase leading-none tracking-tight sm:text-7xl lg:text-8xl"
          >
            The <span className="text-vermilion">Work</span>
          </motion.h2>
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-neutral-500">
          Different brands. Different directions. Take a closer look at the
          work.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-12 md:gap-6 lg:gap-8" data-testid="case-grid">
        {CASE_STUDIES.map((p) => (
          <CaseTile key={p.id} project={p} />
        ))}
      </div>
      <div className="mt-20 md:mt-28" data-testid="brand-in-focus">
        <div className="mb-8 flex flex-col gap-4 md:mb-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-vermilion md:text-xs">
              <span className="inline-block h-2 w-2 bg-vermilion" />
              Single Ads
            </p>
            <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl">
              Brand in Focus<span className="text-vermilion">.</span>
            </h3>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-neutral-500">
            A few of our recent ads — each built to carry its brand on its
            own.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
          {focusAds.map((ad, i) => (
            <motion.div
              key={ad.img}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
              data-testid={`focus-tile-${i + 1}`}
              className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-panel transition-opacity duration-200 active:opacity-80"
            >
              <img
                src={ad.img}
                alt={`${ad.title} single ad`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)", objectPosition: ad.pos || "center" }}
              />
            </motion.div>
          ))}
        </div>
      </div>

      <div className="mt-20 md:mt-28">
        <Marquee />
      </div>
    </section>
  );
}
